'use client'

import {
  ArtifactCanvasActionRow,
  ArtifactCanvasBody,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Skeleton,
  formatClientDate,
  formatClientDateTimeMedium,
  captureListReturnState,
  candidateJobDetailPath,
  navigateWithThreadPreservation,
  applicationStatusBadgeVariant,
  formatApplicationStatus,
  isActiveApplicationStatus,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  ExternalLink,
  LogOut,
  Mail,
  Send,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import { WithdrawApplicationDialog } from './withdraw-application-dialog'
import { useApplicationEvents, type MyApplication } from '../../hooks/use-my-applications'
import type { CandidateApplicationEvent } from '../../hooks/use-my-applications'
import { useWithdrawApplication } from '../../hooks/use-withdraw-application'

const APPLIED_DATE_FORMAT = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
} as const

/** Plain-language "what happens now" line for statuses still in play. Omitted once resolved. */
const NEXT_STEP_COPY: Partial<Record<MyApplication['status'], string>> = {
  applied: "We'll let you know as soon as the employer reviews your application.",
  invited: 'Complete your application so the employer can review it.',
  in_review: 'The hiring team is currently reviewing your application.',
  interview: "You're in the interview stage. Check the timeline below for scheduling details.",
  offer: "You've received an offer. Check your messages for next steps.",
}

function humanizeSlug(slug: string): string {
  return slug
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function decisionCopy(status: string): string {
  switch (status) {
    case 'hired':
      return 'Hired. Congratulations!'
    case 'offer':
      return "You've received an offer for this role."
    case 'rejected':
      return "The employer decided not to move forward with your application."
    default:
      return status ? `Decision recorded: ${humanizeSlug(status)}` : 'A decision was recorded.'
  }
}

function decisionIcon(status: string): LucideIcon {
  if (status === 'hired' || status === 'offer') return CheckCircle2
  if (status === 'rejected') return XCircle
  return CircleDot
}

type TimelineEntry = {
  id: string
  icon: LucideIcon
  label: string
  createdAt: string
  tone: 'default' | 'success' | 'destructive' | 'muted'
}

type InterviewMetadata = {
  round_name?: string
  scheduled_start?: string
  location_type?: string
}

function humanizeLocationType(locationType: string | undefined): string {
  if (!locationType) return ''
  return humanizeSlug(locationType)
}

function timelineEntryForEvent(event: CandidateApplicationEvent): TimelineEntry | null {
  const base = { id: event.id, createdAt: event.created_at }

  if (event.event_type === 'applied') {
    return { ...base, icon: Send, label: 'Application submitted', tone: 'default' }
  }
  if (event.event_type === 'invited') {
    return { ...base, icon: Mail, label: 'Invited to apply', tone: 'default' }
  }
  if (event.event_type === 'stage_changed') {
    const toStage = event.metadata.to_stage
    const label = typeof toStage === 'string' && toStage ? `Moved to ${humanizeSlug(toStage)}` : 'Stage updated'
    return { ...base, icon: ArrowRight, label, tone: 'default' }
  }
  if (event.event_type === 'decision_made') {
    const status = typeof event.metadata.status === 'string' ? event.metadata.status : ''
    const tone = status === 'rejected' ? 'destructive' : status === 'hired' || status === 'offer' ? 'success' : 'default'
    return { ...base, icon: decisionIcon(status), label: decisionCopy(status), tone }
  }
  if (event.event_type === 'withdrawn') {
    return { ...base, icon: LogOut, label: 'You withdrew your application', tone: 'muted' }
  }
  if (event.event_type === 'note_added') {
    const interview = event.metadata.interview as InterviewMetadata | undefined
    if (!interview) return null
    const when = interview.scheduled_start ? formatClientDateTimeMedium(interview.scheduled_start) : ''
    const location = humanizeLocationType(interview.location_type)
    const parts = [when, interview.round_name, location ? `(${location})` : ''].filter(Boolean)
    return { ...base, icon: CalendarClock, label: parts.join(' \u2014 '), tone: 'default' }
  }
  return null
}

const TONE_TEXT_CLASS: Record<TimelineEntry['tone'], string> = {
  default: 'text-foreground',
  success: 'text-success',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
}

const TONE_ICON_CLASS: Record<TimelineEntry['tone'], string> = {
  default: 'text-muted-foreground',
  success: 'text-success',
  destructive: 'text-destructive',
  muted: 'text-muted-foreground',
}

function ApplicationTimeline({
  events,
  isLoading,
  application,
}: {
  events: CandidateApplicationEvent[]
  isLoading: boolean
  application: MyApplication
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-4 w-48" />
      </div>
    )
  }

  const entries = events
    .map(timelineEntryForEvent)
    .filter((entry): entry is TimelineEntry => entry != null)

  const rows =
    entries.length > 0
      ? entries
      : [
          {
            id: 'synthetic-applied',
            icon: Send,
            label: 'Application submitted',
            createdAt: application.applied_at,
            tone: 'default' as const,
          },
        ]

  return (
    <div className="flex flex-col" role="list" aria-label="Application history">
      {rows.map((entry, index) => {
        const Icon = entry.icon
        const isLast = index === rows.length - 1
        return (
          <div key={entry.id} className="flex items-stretch gap-3" role="listitem">
            <div className="flex w-5 shrink-0 flex-col items-center">
              <Icon className={`mt-0.5 h-4 w-4 ${TONE_ICON_CLASS[entry.tone]}`} aria-hidden />
              {!isLast ? <div className="mt-1 w-px flex-1 bg-border" /> : null}
            </div>
            <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-4'}`}>
              <p className={`text-sm font-medium ${TONE_TEXT_CLASS[entry.tone]}`}>{entry.label}</p>
              <p className="text-xs text-muted-foreground">
                {formatClientDate(entry.createdAt, APPLIED_DATE_FORMAT)}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Top Act zone — shared shell with employer applicant panel; actions stay candidate-specific. */
function ApplicationStatusActionBar({
  jobPostingHref,
  canWithdraw,
  withdrawPending,
  onViewJobPosting,
  onWithdraw,
}: {
  jobPostingHref: string | null
  canWithdraw: boolean
  withdrawPending: boolean
  onViewJobPosting: () => void
  onWithdraw: () => void
}) {
  if (!jobPostingHref && !canWithdraw) return null

  return (
    <ArtifactCanvasActionRow aria-label="Application actions">
      {jobPostingHref ? (
        <Button type="button" variant="outline" size="sm" pressFeedback onClick={onViewJobPosting}>
          <ExternalLink className="h-4 w-4" aria-hidden />
          View job posting
        </Button>
      ) : null}
      {canWithdraw ? (
        <Button
          type="button"
          variant="outlineDestructive"
          size="sm"
          pressFeedback
          disabled={withdrawPending}
          onClick={onWithdraw}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Withdraw
        </Button>
      ) : null}
    </ArtifactCanvasActionRow>
  )
}

export type ApplicationStatusCanvasProps = {
  application: MyApplication
  onClose: () => void
  onBeforeNavigateAway?: () => void
  onWithdrawn?: () => void
}

export function ApplicationStatusCanvas({
  application,
  onClose,
  onBeforeNavigateAway,
  onWithdrawn,
}: ApplicationStatusCanvasProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [coverLetterOpen, setCoverLetterOpen] = useState(false)
  const { events, isLoading: eventsLoading } = useApplicationEvents(application.id)
  const withdraw = useWithdrawApplication({
    applicationId: application.id,
    onWithdrawn,
  })

  const canWithdraw = isActiveApplicationStatus(application.status)
  const nextStep = NEXT_STEP_COPY[application.status]
  const hasCoverLetter = Boolean(application.cover_letter?.trim())

  const jobPostingHref = application.opportunity_id
    ? candidateJobDetailPath({
        public_slug: application.opportunity_slug,
        id: application.opportunity_id,
      })
    : null

  const handleViewJobPosting = () => {
    if (!jobPostingHref) return
    // Signal the list page to skip clearing `?application=` — that setSearchParams
    // races with this navigation and can cancel the job-detail route.
    onBeforeNavigateAway?.()
    const returnParams = new URLSearchParams(location.search)
    returnParams.delete('application')
    const returnSearch = returnParams.toString()
    navigateWithThreadPreservation(navigate, jobPostingHref, searchParams, {
      state: captureListReturnState({
        pathname: location.pathname,
        search: returnSearch ? `?${returnSearch}` : '',
      }),
    })
    onClose()
  }

  return (
    <>
      <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col gap-6 space-y-0 overflow-y-auto">
        <ApplicationStatusActionBar
          jobPostingHref={jobPostingHref}
          canWithdraw={canWithdraw}
          withdrawPending={withdraw.isPending}
          onViewJobPosting={handleViewJobPosting}
          onWithdraw={withdraw.openDialog}
        />

        <section className="space-y-3 rounded-xl border border-border/80 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Badge variant={applicationStatusBadgeVariant(application.status)}>
              {formatApplicationStatus(application.status)}
            </Badge>
            <span className="text-xs text-muted-foreground tabular-nums">
              Applied {formatClientDate(application.applied_at, APPLIED_DATE_FORMAT)}
            </span>
          </div>
          {application.current_stage ? (
            <p className="text-sm text-muted-foreground">
              Current stage: {application.current_stage.stage_name}
            </p>
          ) : null}
          {nextStep ? <p className="text-sm text-foreground">{nextStep}</p> : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Application history</h3>
          <ApplicationTimeline events={events} isLoading={eventsLoading} application={application} />
        </section>

        {hasCoverLetter ? (
          <Collapsible open={coverLetterOpen} onOpenChange={setCoverLetterOpen}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-auto min-h-10 w-full justify-between gap-2 px-3 py-2 text-left font-medium"
              >
                Cover letter
                {coverLetterOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 rounded-md border border-dashed border-border/80 p-3">
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {application.cover_letter}
              </p>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </ArtifactCanvasBody>

      {canWithdraw ? <WithdrawApplicationDialog {...withdraw.dialogProps} /> : null}
    </>
  )
}
